/**
 * Unified decision history timeline for service request review cards.
 * Renders selections, approvals, rejections, and member responses chronologically.
 * @agentic connections
 */
import { Badge, Group, Paper, Stack, Text } from "@mantine/core";
import type { DecisionNoteFields } from "@benchmark/core/resolvers";

/**
 * Props for rendering decision history.
 * @agentic connections
 */
type PriorReviewsSectionProps = {
  decisionHistory: DecisionNoteFields[];
};

/** Derives badge color from a decision note. */
const getBadgeColor = (note: DecisionNoteFields): string => {
  if (note.source === "member") {
    return note.decision === "accepted" ? "green" : "orange";
  }
  if (note.decision === "approved") return "green";
  if (note.decision === "denied") return "red";
  return "orange";
};

/** Derives badge label from a decision note. */
const getBadgeLabel = (note: DecisionNoteFields): string => {
  if (note.source === "member") {
    if (note.decision === "accepted") return "Member Accepted";
    return "Member";
  }
  if (note.decision === "approved") return "Approved";
  if (note.decision === "denied") return "Denied";
  return "More Info";
};

/**
 * Displays the decision history timeline for a service request.
 * @agentic connections
 */
export const PriorReviewsSection = ({
  decisionHistory,
}: PriorReviewsSectionProps) => {
  if (decisionHistory.length === 0) {
    return null;
  }

  return (
    <Paper p="xs" bg="gray.0" radius="sm">
      <Stack gap={4}>
        <Text size="xs" fw={600} c="dimmed" tt="uppercase">
          Decision History ({decisionHistory.length})
        </Text>
        {decisionHistory.map((review, index) => (
          <Group key={index} gap="xs">
            <Badge size="xs" variant="light" color={getBadgeColor(review)}>
              {getBadgeLabel(review)}
            </Badge>
            <Text size="xs" c="dimmed" lineClamp={1}>
              {review.rationale || "(no rationale)"}
            </Text>
            {review.authorString && (
              <Text size="xs" c="dimmed">
                — {review.authorString}
              </Text>
            )}
          </Group>
        ))}
      </Stack>
    </Paper>
  );
};
